/**
 * @fileoverview Chat API fetch functions.
 * @module logic/chat/chatApi
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { refreshToken, signOut } from "../../utils/common.js";
import { initializeUsersPage } from "../user/index.js";
import { getUserConnectionRequests } from "../connection/index.js";
import { getGenericErrorMessage } from "../../utils/genericErrorMessages.js";
import {
  COMPANY_NOT_FOUND,
  HTTP_CREATED,
  HTTP_OK,
} from "./chatConstants.js";

/**
 * Normalizes chat/message API payloads from either API Gateway ({ body: string })
 * or direct JSON ({ success, data }).
 * @param {unknown} payload
 * @returns {Object|Array|null}
 */
export const parseApiGatewayJsonBody = (payload) => {
  if (payload == null) {
    return null;
  }
  if (typeof payload.body === "string") {
    return JSON.parse(payload.body);
  }
  if (payload.body && typeof payload.body === "object") {
    return payload.body;
  }
  return payload;
};

/**
 * Fetches all chats for a specific user.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of chat objects or empty array on error
 */
export const getUserChats = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}websocket/user/${userId}/chats`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getUserChats(userId, navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error fetching user chats:", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches all messages for a specific chat.
 * @async
 * @param {string} chatId - Chat identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of message objects
 */
export const getChatMessages = async (chatId, navigate) => {
  logger.info("Fetching chat messages for chat in getChatMessages:", {
    data: chatId,
  });

  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_ROOT_API
      }websocket/chat/${encodeURIComponent(chatId)}/messages`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getChatMessages(chatId, navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error fetching chat messages:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches all active WebSocket connections (online users).
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of active connection objects
 */
export const getActiveConnections = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}websocket/connections`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getActiveConnections(navigate);
    }
    if (!response.ok || data?.code === "FORBIDDEN") {
      return { statusCode: response.status, data: [] };
    }
    return data;
  } catch (error) {
    logger.error("Error fetching active connections:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Marks all messages in a chat as read.
 * @async
 * @param {string} chatId - Chat identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|boolean>} API response or false on error
 */
export const markChatAsRead = async (chatId, navigate) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}websocket/chat/${chatId}/read`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await markChatAsRead(chatId, navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error marking chat as read:", { err: sanitizeError(error) });
    return false;
  }
};

/**
 * Fetches and sets active WebSocket connections.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setActiveConnections - State setter for connections
 * @returns {Promise<void>}
 */
export const fetchActiveConnections = async (
  navigate,
  setActiveConnections,
) => {
  try {
    const connectionsData = await getActiveConnections(navigate);
    logger.info("Active connections data:", { data: connectionsData });
    if (connectionsData.statusCode === HTTP_OK) {
      setActiveConnections(connectionsData.data || []);
    } else {
      logger.error("Failed to fetch active connections");
      setActiveConnections([]);
    }
  } catch (error) {
    logger.error("Error fetching active connections:", {
      err: sanitizeError(error),
    });
    setActiveConnections([]);
  }
};

/**
 * Fetches users and their companies from the Users page data.
 * Fallback for APIs whose chat list does not include participants.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setUsers - State setter for users
 * @param {Function} setCompanies - State setter for companies keyed by RFC
 * @returns {Promise<Array>} Array of users
 */
export const fetchUsers = async (navigate, setUsers, setCompanies) => {
  try {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setUsers([]);
      setCompanies({});
      return [];
    }
    const page = await initializeUsersPage(userId, navigate);
    const usersData = Array.isArray(page?.users) ? page.users : [];
    setUsers(usersData);
    setCompanies(page?.companies || {});
    return usersData;
  } catch (error) {
    logger.error("Error fetching users:", { err: sanitizeError(error) });
    setUsers([]);
    setCompanies({});
    return [];
  }
};

/**
 * Reads the participants returned with the chat list.
 * @param {Object|Array|null} parsedBody - Parsed chat list payload
 * @returns {{ users: Array, companies: Object }|null} null when the payload has no participants
 */
export const getChatParticipants = (parsedBody) => {
  if (!parsedBody || !Array.isArray(parsedBody.users)) {
    return null;
  }
  const companies =
    parsedBody.companies && typeof parsedBody.companies === "object"
      ? parsedBody.companies
      : {};
  return { users: parsedBody.users, companies };
};

/**
 * Fetches and sets chats for a specific user, plus the chat participants when returned.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setChats - State setter for chats
 * @param {Function} [setChatParticipants] - Receives { users, companies } when the API returns them
 * @returns {Promise<{ users: Array, companies: Object }|null>} Participants, or null when absent
 */
export const fetchUserChats = async (
  userId,
  navigate,
  setChats,
  setChatParticipants,
) => {
  logger.info("Fetching chats for user:", { data: userId });

  try {
    const chatsData = await getUserChats(userId, navigate);
    logger.info("Chats data:", { data: chatsData });
    const parsedBody = parseApiGatewayJsonBody(chatsData);
    if (parsedBody?.success && Array.isArray(parsedBody.data)) {
      setChats(parsedBody.data);
    } else if (Array.isArray(parsedBody)) {
      setChats(parsedBody);
    } else {
      logger.info("No chats found or API returned empty data");
      setChats([]);
    }
    const participants = getChatParticipants(parsedBody);
    if (participants && setChatParticipants) {
      setChatParticipants(participants);
    }
    return participants;
  } catch (error) {
    logger.error("Error fetching chats:", { err: sanitizeError(error) });
    setChats([]);
    return null;
  }
};

/**
 * Fetches and normalizes connection requests for the current user.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setConnectionRequests - State setter for requests
 * @param {Function} setConnectionRequestsLoading - State setter for loading indicator
 * @returns {Promise<void>}
 */
export const fetchConnectionRequests = async (
  navigate,
  setConnectionRequests,
  setConnectionRequestsLoading,
) => {
  setConnectionRequestsLoading(true);

  try {
    const userId = localStorage.getItem("userId");
    const requestsData = await getUserConnectionRequests(userId, navigate);

    logger.info("Requests data:", { data: requestsData });

    if (
      requestsData.statusCode === HTTP_CREATED &&
      requestsData.data &&
      requestsData.data.length > 0
    ) {
      // Backend now returns enriched requests (senderUser, senderUserCompany, etc.)
      // For backward compatibility in the UI, derive flat fields used by components
      const normalizedRequests = requestsData.data.map((request) => {
        const senderUser = request.senderUser || {};
        const senderCompany = request.senderUserCompany || {};

        const derivedCompanyName =
          senderCompany.nombreComercial ||
          senderCompany.nombre ||
          senderCompany.razonSocial ||
          COMPANY_NOT_FOUND;

        return {
          ...request,
          nombre: request.nombre || senderUser.nombre || "",
          correoElectronico:
            request.correoElectronico || senderUser.correoElectronico || "",
          empresa: request.empresa || derivedCompanyName,
          RFC: request.RFC || senderUser.RFC || request.senderCompanyRFC || "",
        };
      });

      logger.info("Normalized connection requests:", {
        data: normalizedRequests,
      });
      setConnectionRequests(normalizedRequests);
    } else {
      logger.info("No connection requests found");
      setConnectionRequests([]);
    }
  } catch (error) {
    logger.error("Error fetching connection requests:", {
      err: sanitizeError(error),
    });
    setConnectionRequests([]);
  } finally {
    setConnectionRequestsLoading(false);
  }
};

/**
 * Reassigns a chat from one user to another within the same company.
 * @async
 * @param {string} chatId - Chat identifier
 * @param {string} userReassigningId - User performing the reassignment
 * @param {string} userBeingReassignedId - Current chat owner
 * @param {string} selectedUserToReassignId - New chat owner
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Result with success boolean and data/message
 */
export const reassignChat = async (
  chatId,
  userReassigningId,
  userBeingReassignedId,
  selectedUserToReassignId,
  navigate,
) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({
      chatId,
      userReassigningId,
      userBeingReassignedId,
      selectedUserToReassignId,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}chat/assignment`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return {
          success: false,
          message: getGenericErrorMessage("SESSION_EXPIRED"),
        };
      }
      return await reassignChat(
        chatId,
        userReassigningId,
        userBeingReassignedId,
        selectedUserToReassignId,
        navigate,
      );
    }

    if (!response.ok) {
      return {
        success: false,
        message:
          data?.message || getGenericErrorMessage("ERROR_REASSIGNING_CHAT"),
      };
    }

    return { success: true, data };
  } catch (error) {
    logger.error("Error reassigning chat:", { err: sanitizeError(error) });
    return {
      success: false,
      message: getGenericErrorMessage("ERROR_REASSIGNING_CHAT"),
    };
  }
};
